import { type ReactNode } from "react";
import type { components } from "@oathera/contract/api";
import {
  estimateSuffix,
  measureDescription,
  type Measure,
} from "../../components/index.ts";
import type { DecisionSummary } from "./use-decisions.ts";
import { quantityMeasure } from "./vocab.ts";

/**
 * Decision latency (task 11.6; design 12.2 "decision latency"; 12.3, 12.4).
 *
 * The contract's `latency` carries exactly three percentiles — p50, p95, p99 — each a `Quantity`
 * that says whether it is exact or an estimate (percentiles are typically histogram estimates, so
 * these usually read "est."). The panel renders them as three tiles, each labelled with its
 * confidence and shown in milliseconds; a null percentile (no data) is a text em dash and is styled
 * `unverifiable` (grey, never green; design 12.3), never a fabricated zero. The totals do not define
 * a latency time-series, so this is tiles rather than a chart — the design allows either, and tiles
 * are the honest choice for three contract-provided percentiles.
 */

type Latency = DecisionSummary["latency"];
type Quantity = components["schemas"]["Quantity"];

interface PercentileSpec {
  readonly key: keyof Latency;
  readonly title: string;
}

const PERCENTILES: readonly PercentileSpec[] = [
  { key: "p50_ms", title: "Latency p50" },
  { key: "p95_ms", title: "Latency p95" },
  { key: "p99_ms", title: "Latency p99" },
];

const MS = (v: number): string => v.toLocaleString(undefined, { maximumFractionDigits: 1 });

export function LatencyPanel(props: { latency: Latency }): ReactNode {
  return (
    <section className="dec-latency" aria-label="Decision latency">
      <h2 className="dec-section__title">Decision latency</h2>
      <div className="dec-latency__grid">
        {PERCENTILES.map((spec) => (
          <LatencyTile key={spec.key} spec={spec} quantity={props.latency[spec.key]} />
        ))}
      </div>
    </section>
  );
}

function LatencyTile(props: { spec: PercentileSpec; quantity: Quantity }): ReactNode {
  const { spec, quantity } = props;
  const measure: Measure = quantityMeasure(quantity);
  const value = quantity.value;

  const display = value === null ? "n/a" : `${MS(value)} ms${estimateSuffix(measure)}`;
  const a11y =
    value === null
      ? `${spec.title}: no data (unverifiable).`
      : `${spec.title}: ${MS(value)} milliseconds, ${measureDescription(measure)}.`;

  return (
    <div className="dec-tile" aria-label={a11y}>
      <span className="dec-tile__title">{spec.title}</span>
      <span className="dec-tile__value" data-measure={measure}>
        {display}
      </span>
    </div>
  );
}
